import produce from "immer";
// CONSTS
const initialFilterState = {};
/**
 * Pattern for stripping numbers and spaces from a string.
 * @NOTE You still need to provide an arg for the replacement param: foo.replace(stripNumAndSpacePattern, "")
 */
export const stripNumAndSpacePattern = /\s|[0-9]/g;
// ENUMS
// The RecipeObjectPaths are the recipe[path] used to locate the filterables in the recipe object.
var RecipeObjectPaths;
(function (RecipeObjectPaths) {
    RecipeObjectPaths["PROGRAM_MEAL_ID"] = "ProgramMealId";
    RecipeObjectPaths["RECIPE_FLAGS"] = "RecipeFlags";
    RecipeObjectPaths["RECIPE_TYPE_NAMES"] = "RecipeTypeNames";
})(RecipeObjectPaths || (RecipeObjectPaths = {}));
var ValueToCompare;
(function (ValueToCompare) {
    ValueToCompare["LABEL"] = "Label";
    ValueToCompare["VALUE"] = "Value";
})(ValueToCompare || (ValueToCompare = {}));
var FilterableModules;
(function (FilterableModules) {
    // This does not use the capitalization convention for enums because it needs to match incoming vars.
    FilterableModules["MealType"] = "MEAL_SIZE";
    FilterableModules["Omissions"] = "OMISSION";
    FilterableModules["RecipeFlag"] = "RECIPE_FLAG";
    FilterableModules["RecipeType"] = "RECIPE_TYPE";
})(FilterableModules || (FilterableModules = {}));
export var FilterTypesBaseKeys;
(function (FilterTypesBaseKeys) {
    // This does not use the capitalization convention for enums because it needs to match incoming vars.
    // The keys themselves have a numeric value appended. A regex replacement to strip the numerics
    // can be used to test: e.g., foo.replace(/\s|[0-9]/g, '')).
    FilterTypesBaseKeys["MealType"] = "MealType";
    FilterTypesBaseKeys["RecipeFlag"] = "RecipeFlag";
    FilterTypesBaseKeys["RecipeType"] = "RecipeType";
})(FilterTypesBaseKeys || (FilterTypesBaseKeys = {}));
// Display text used in the module.
export var FilterModuleText;
(function (FilterModuleText) {
    FilterModuleText["BY"] = "By";
    FilterModuleText["FILTER"] = "Filter";
    FilterModuleText["FILTER_OPTIONS"] = "Filter options";
    FilterModuleText["NONE"] = "None";
    FilterModuleText["RESET_FILTERS"] = "Reset filters";
})(FilterModuleText || (FilterModuleText = {}));
export var MiscEnums;
(function (MiscEnums) {
    MiscEnums["ALL"] = "All";
})(MiscEnums || (MiscEnums = {}));
export var InitialRecipeFilterVisibilityEnum;
(function (InitialRecipeFilterVisibilityEnum) {
    InitialRecipeFilterVisibilityEnum["Expanded"] = "Expanded";
    InitialRecipeFilterVisibilityEnum["Collapsed"] = "Collapsed";
})(InitialRecipeFilterVisibilityEnum || (InitialRecipeFilterVisibilityEnum = {}));
// HELPER FUNCTIONS
/**
 * Change handler for select-inputs.
 * @param setFilterState Filter state setter from filter context.
 * @returns The new filter state.
 */
export const handleFilterInputChange = (setFilterState) => (event, child) => {
    const { name, value } = event.target;
    // If the value is not an array but not an empty string, we put the string in an array.
    // If the value from a single-select is an empty string, we set that value to an empty array.
    const valueArray = Array.isArray(value) ? value : !!value ? [value] : [];
    setFilterState((prev) => {
        const newFilterState = produce(prev, (draft) => {
            if (!valueArray.length) {
                delete draft[name];
            }
            else {
                draft[name] = valueArray;
            }
        });
        return newFilterState;
    });
};
/**
 * Curried function for chip delete action.
 * @param filterState (init curried param) the current filterState (this should be able to be removed)
 * @param setFilterState (init curried param) state setter
 * @param chip The chip to delete as an object and not stringified
 * @param event - not used, but left for future use (just in case)
 * @returns void
 */
export const handleChipDelete = (filterState, setFilterState) => (chip) => (_) => {
    setFilterState((prev) => {
        const filterStateKey = buildFilterID(chip.immerFilterType, chip.immerFilterSequence);
        const indexOfChipToDelete = filterState[filterStateKey].indexOf(stringifyAndMaintainKeyOrder(chip));
        const newFilterState = produce(prev, (draft) => {
            var _a, _b;
            if (((_a = draft[filterStateKey]) === null || _a === void 0 ? void 0 : _a.length) <= 1)
                delete draft[filterStateKey];
            if (((_b = draft[filterStateKey]) === null || _b === void 0 ? void 0 : _b.length) >= 2) {
                draft[filterStateKey].splice(indexOfChipToDelete, 1);
            }
        });
        return newFilterState;
    });
};
/**
 * Removes all filters.
 * @param setFilterState Filter state setter.
 * @returns void
 */
export const resetRecipeFilterState = (setFilterState) => (_) => {
    setFilterState(initialFilterState);
};
/**
 * Ensures that a stringified object maintains key:value pair order.
 * @param obj The object to be stringified.
 * @returns The stringified object.
 */
export const stringifyAndMaintainKeyOrder = (obj) => JSON.stringify(obj, Object.keys(obj).sort());
/**
 * Func to create and maintain consistency for filter id to be used throughout the component.
 * @param filterType The incoming filter type.
 * @param filterSequence The incoming filter sequence.
 * @returns A singe value representing both the filter type and sequence as a single string.
 * We remove any spaces from the filter type because strings with spaces may cause unexpected issues when used as object keys.
 */
export const buildFilterID = (filterType, filterSequence) => {
    const fKey = filterType.replace(/\s/g, "");
    return `${fKey}${filterSequence.toString()}`;
};
/**
 *
 * @param filterState The current filter state from the filter context.
 * @param baseKey The filter input/type (recipe flags, etc.)
 * @param valueToCompare Pulls the selected filter values.
 * @returns The filter types.
 */
const getFilterTypesToApply = (filterState, baseKey, valueToCompare) => {
    const typeKeys = Object.keys(filterState).filter((key) => key.replace(stripNumAndSpacePattern, "") === baseKey);
    if (!typeKeys.length)
        return [];
    return typeKeys.map((k) => filterState[k].map((f) => {
        // IF the filter type is a meal type, we convert the string to a number.
        return baseKey === FilterTypesBaseKeys.MealType ? +JSON.parse(f)[valueToCompare] : JSON.parse(f)[valueToCompare];
    }));
};
/**
 * Apply the selected filters.
 * @param recipe The specific recipe for comparison.
 * @param filterTypes The filter types/values selected by the user.
 * @param objectPath The location of the relevant values for comparison on the recipe.
 * @returns A boolean value denoting whether or not a value is a match for a filter.
 */
const applyFilters = (recipe, filterTypes, recipeObjectPath) => {
    let isMatched = true;
    for (let i = 0; i <= filterTypes.length - 1; i++) {
        // Use a switch instead of if... else loop because the number of filters will be at least 4.
        switch (recipeObjectPath) {
            case RecipeObjectPaths.PROGRAM_MEAL_ID:
                // Numerical comparison.
                isMatched = filterTypes[i].includes(recipe[recipeObjectPath]);
                break;
            case RecipeObjectPaths.RECIPE_FLAGS:
                // String comparison.
                // FIXME: This will change when the flag filter is properly using k:v pairs.
                isMatched = recipe[recipeObjectPath].some((r) => filterTypes[i].includes(r.FlagValue));
                break;
            case RecipeObjectPaths.RECIPE_TYPE_NAMES:
                // String comparison.
                isMatched = recipe[recipeObjectPath].some((r) => filterTypes[i].map((f) => f.toLocaleLowerCase()).includes(r.toLocaleLowerCase()));
                break;
            default:
                // This case "cannot" occur, but just in case.
                isMatched = false;
                break;
        }
        if (!isMatched)
            return false;
    }
    return isMatched;
};
/**
 *
 * @param recipe The specific recipe for comparison.
 * @param filterState The current filter state from the filter context.
 * @returns boolean
 */
export const applyRecipeModuleFilters = (recipe, filterState) => {
    // Might want to move get fns outside the apply fn.
    const recipeTypeFilters = getFilterTypesToApply(filterState, FilterTypesBaseKeys.RecipeType, ValueToCompare.LABEL);
    const mealTypeFilters = getFilterTypesToApply(filterState, FilterTypesBaseKeys.MealType, ValueToCompare.VALUE);
    const recipeFlagFilters = getFilterTypesToApply(filterState, FilterTypesBaseKeys.RecipeFlag, ValueToCompare.VALUE);
    const isRecipeTypeMatch = applyFilters(recipe, recipeTypeFilters, RecipeObjectPaths.RECIPE_TYPE_NAMES);
    if (isRecipeTypeMatch === false)
        return false;
    const isMealTypeMatch = applyFilters(recipe, mealTypeFilters, RecipeObjectPaths.PROGRAM_MEAL_ID);
    if (isMealTypeMatch === false)
        return false;
    const isRecipeFlagMatch = applyFilters(recipe, recipeFlagFilters, RecipeObjectPaths.RECIPE_FLAGS);
    if (isRecipeFlagMatch === false)
        return false;
    // TODO Omissions filter operates differently from the others; omissions should be /exclusionary/.
    // omissions
    // get omissions filters
    // isOmissionTypeMatch
    return true;
};
